/**
 * BudgetSemaphoreCard — card de orçamento com semáforo de utilização (SP3).
 *
 * Cor do semáforo (§5.11(a)(c) da spec):
 *  - verde  (green.6)  → pct_used < alert_at_pct (default 80)
 *  - amarelo (yellow.6) → alert_at_pct ≤ pct_used < 100
 *  - vermelho (red.6)  → pct_used ≥ 100
 *  - neutro (gray)     → pct_used === null (sem orçamento)
 *
 * Usa `RingProgress` do Mantine para visualizar utilização percentual.
 * Cores apenas via tokens DS/Mantine. Sem fetch interno.
 */
import { Group, RingProgress, Stack, Text } from '@mantine/core'
import { WidgetCard } from '@/components/ui'
import { formatMoney } from '@/components/finops/formatMoney'

export interface BudgetSemaphoreCardProps {
  /** Percentual utilizado (0-∞). null = sem orçamento cadastrado. */
  pct_used: number | null
  /** Orçamento total em USD. */
  budget_usd: number
  /** Valor consumido em USD. */
  amount_usd: number
  /** Limiar de alerta amarelo (default 80). */
  alert_at_pct?: number
  /** Rótulo do card. */
  title?: string
}

type SemaphoreColor = 'green.6' | 'yellow.6' | 'red.6' | 'gray.5'

function getSemaphoreColor(
  pctUsed: number | null,
  alertAt: number,
): SemaphoreColor {
  if (pctUsed === null) return 'gray.5'
  if (pctUsed >= 100) return 'red.6'
  if (pctUsed >= alertAt) return 'yellow.6'
  return 'green.6'
}

function getSemaphoreLabel(pctUsed: number | null, alertAt: number): string {
  if (pctUsed === null) return 'Sem orçamento'
  if (pctUsed >= 100) return 'Acima do limite'
  if (pctUsed >= alertAt) return 'Atenção'
  return 'Dentro do orçamento'
}

export function BudgetSemaphoreCard({
  pct_used,
  budget_usd,
  amount_usd,
  alert_at_pct = 80,
  title = 'Orçamento',
}: BudgetSemaphoreCardProps) {
  const color = getSemaphoreColor(pct_used, alert_at_pct)
  const statusLabel = getSemaphoreLabel(pct_used, alert_at_pct)

  // RingProgress aceita valor 0-100; clampamos para não ultrapassar o anel visualmente
  const ringValue = pct_used === null ? 0 : Math.min(pct_used, 100)

  return (
    <WidgetCard accent="none" p="md">
      <Stack gap="xs">
        <Text fw={600} size="sm" c="var(--ai-brand-azul)">
          {title}
        </Text>

        <Group align="center" gap="md" wrap="nowrap">
          <RingProgress
            size={80}
            thickness={8}
            roundCaps
            sections={[{ value: ringValue, color }]}
            label={
              <Text
                ta="center"
                fw={700}
                size="xs"
                c={color}
                style={{ lineHeight: 1.1 }}
              >
                {pct_used === null ? '—' : `${pct_used.toFixed(0)}%`}
              </Text>
            }
          />

          <Stack gap={2} style={{ flex: 1 }}>
            <Text size="xs" c="dimmed">
              Consumido
            </Text>
            <Text fw={700} size="sm">
              {formatMoney(amount_usd)}
            </Text>

            <Text size="xs" c="dimmed" mt={4}>
              Total orçado
            </Text>
            <Text size="sm">
              {budget_usd > 0 ? formatMoney(budget_usd) : '—'}
            </Text>
          </Stack>
        </Group>

        <Text size="xs" c={color} fw={600}>
          {statusLabel}
        </Text>
      </Stack>
    </WidgetCard>
  )
}
